/**
 * components/PengaturanModal.js
 * Pengaturan (Settings, Phase T2-settings) — a small modal, opened from a sidebar entry
 * (App.js, not a nav page), editing `data.settings.company`: the letterhead printed on every
 * PUSAT document (Invoice Pusat, Surat Jalan Pusat, Checklist). This moved company details OUT
 * of the hardcoded `utils/company.js` constant specifically so a typo or a wrong NPWP digit can
 * be fixed from the app, by a non-technical user, without a code change — that file now only
 * supplies the DEFAULT value the v13->v14 migration seeds into storage on first upgrade.
 *
 * **Phase T2d** added a `cabangName` field here — **removed again by the multi-branch cabang
 * feature**: there turned out to be three real cabang branches (MMC/AMIN/TMM), not one, so a
 * single flat settings string was never going to be enough. Cabang branch names became a real
 * managed list (`data.branches`, `utils/branches.js`) instead — `onSave` reverts to its
 * original single-argument shape.
 *
 * **Pengaturan-as-single-home-for-settings pass**: branch management (add/rename/typed-HAPUS
 * delete, unchanged) MOVES here from a "Kelola Cabang" button that used to live on the
 * Penjualan page — see the "Cabang" block below. This is a genuinely different SAVE STYLE from
 * the company form above it (branch add/rename/delete commit IMMEDIATELY, same as before —
 * never bundled into this component's own `onSave`), so it is laid out as its own block, below
 * this component's `<form>`, with its own heading and its own single "Kelola Cabang" button —
 * opening the unmodified `KelolaCabangModal`, which still does all the real committing. Clicking
 * this form's own Batal/Simpan never touches a branch — there is nothing for either to undo, a
 * branch change already happened the moment it was made.
 *
 * **Bank account per location, same pass**: `data.branches[].bankAccount` (a branch's own,
 * edited inside `KelolaCabangModal` — see that file) and `data.settings.bankAccount` (Pusat's,
 * since Pusat has no branch record — `branchId: null` everywhere in this app). Pusat's account
 * sits in the SAME company `<form>` as Nama/Alamat/NPWP (it lives on `settings`, "alongside the
 * company details" per the storage split), so it follows THAT section's save style: typed, then
 * committed on Simpan, discarded on Batal — not immediate. Free text, never validated/
 * reformatted, may be empty. `utils/branches.js`'s `bankAccountFor(data, branchId)` is the one
 * function every future reader should use to resolve either location's account — this modal
 * itself never reads it, since it always has the right value handed to it as a prop.
 *
 * **Default Jatuh Tempo (Hari) field added** (Tempo Pembayaran redesign): a shop-wide numeric
 * setting — the day-count `TransactionFormModal` defaults a NEW Belum Lunas transaction's due
 * date to (`data.settings.defaultDueDays`), replacing a hardcoded `14`. Not letterhead, but the
 * "deliberately the only thing in this modal" comment above is about resisting unrelated
 * SCOPE (e.g. it isn't a general settings page), not a literal field-count cap — this is still
 * one shop-wide setting edited from the one sidebar "Pengaturan" entry. `onSave` STAYS a single
 * argument (a second positional arg was tried once for `cabangName` in T2d and reverted — see
 * above) — this field, and `bankAccount` above, are simply flattened into the same object the
 * other fields already build.
 *
 * Free text/number fields; there is no validation beyond a numeric fallback for Default Jatuh
 * Tempo, since the letterhead fields are just printed text, not structured data a downstream
 * engine depends on.
 *
 * `addressLines` (a string[]) is edited as one plain multi-line textarea rather than a
 * chip/repeatable-row editor — the simplest possible UI for "several lines of free text" and
 * the least likely to confuse a non-technical user. On save, the textarea's value is split on
 * newlines, each line trimmed, and empty lines dropped. `bankAccount` gets the same kind of
 * plain multi-line textarea, but is NEVER split/trimmed/dropped — it's stored as one exact block
 * of text (an account number must print one day exactly as typed).
 */
import React, { useState } from "react";
import { useModalEsc, backdropClose } from "../utils/modalStack";
import KelolaCabangModal from "./KelolaCabangModal";

export default function PengaturanModal({
  company, bankAccount, defaultDueDays,
  branches = [], salespeople = [], transactions = [],
  onSave, onAddBranch, onUpdateBranch, onDeleteBranch, onClose,
  // Multi-device sync, step 4: this computer's own name. `null` = not running on the shared
  // online books, so the section is hidden. Saved immediately, like Cabang — never part of the
  // company <form>'s Simpan/Batal, since it belongs to this computer, not to the shared books.
  deviceLabel = null, onSaveDeviceLabel,
}) {
  const [deviceText, setDeviceText] = useState(deviceLabel || "");
  const deviceDirty = deviceLabel !== null && deviceText.trim() !== (deviceLabel || "");
  const [name, setName] = useState(company?.name || "");
  const [addressText, setAddressText] = useState((company?.addressLines || []).join("\n"));
  const [npwp, setNpwp] = useState(company?.npwp || "");
  const [bankAccountText, setBankAccountText] = useState(bankAccount || "");
  const [dueDaysText, setDueDaysText] = useState(String(defaultDueDays ?? 14));
  const [kelolaCabangOpen, setKelolaCabangOpen] = useState(false);

  useModalEsc(onClose);

  const submit = (e) => {
    e.preventDefault();
    const addressLines = addressText.split("\n").map((line) => line.trim()).filter((line) => line !== "");
    onSave({
      name: name.trim(), addressLines, npwp: npwp.trim(),
      bankAccount: bankAccountText,
      defaultDueDays: Math.max(1, Math.round(Number(dueDaysText)) || 14),
    });
  };

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onMouseDown={backdropClose(onClose)}>
      <div className="modal-box" onMouseDown={(e) => e.stopPropagation()}>
        <h2 className="modal-title">Pengaturan</h2>
        <div className="page-sub" style={{ marginBottom: "var(--gap-lg)" }}>
          Detail perusahaan ini dicetak di kop setiap dokumen (Invoice, Surat Jalan, dll).
        </div>

        <form onSubmit={submit}>
          <div className="form-row">
            <label className="form-label" htmlFor="pengaturan-nama">Nama Perusahaan</label>
            <input id="pengaturan-nama" className="form-input" value={name} autoFocus
              onChange={(e) => setName(e.target.value)} />
          </div>

          <div className="form-row">
            <label className="form-label" htmlFor="pengaturan-alamat">Alamat</label>
            <textarea id="pengaturan-alamat" className="form-input" rows={3} value={addressText}
              onChange={(e) => setAddressText(e.target.value)} />
            <div className="form-hint">Satu baris per alamat.</div>
          </div>

          <div className="form-row">
            <label className="form-label" htmlFor="pengaturan-npwp">NPWP</label>
            <input id="pengaturan-npwp" className="form-input" value={npwp}
              onChange={(e) => setNpwp(e.target.value)} />
          </div>

          <div className="form-row">
            <label className="form-label" htmlFor="pengaturan-rekening">Rekening Bank (Pusat)</label>
            <textarea id="pengaturan-rekening" className="form-input" rows={3} value={bankAccountText}
              onChange={(e) => setBankAccountText(e.target.value)} />
            <div className="form-hint">
              Opsional. Teks bebas — nama bank, nomor rekening, dan atas nama ditulis sendiri,
              disimpan persis seperti diketik.
            </div>
          </div>

          <div className="form-row">
            <label className="form-label" htmlFor="pengaturan-due-days">Default Jatuh Tempo (Hari)</label>
            <input id="pengaturan-due-days" className="form-input" type="number" min="1" value={dueDaysText}
              onChange={(e) => setDueDaysText(e.target.value)} />
            <div className="form-hint">Dipakai sebagai Tempo Pembayaran awal saat transaksi Belum Lunas baru dibuat.</div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn" onClick={onClose}>Batal</button>
            <button type="submit" className="btn btn-primary">Simpan</button>
          </div>
        </form>

        {/* Own separate block, own heading, own single control — deliberately outside the
            <form> above. Branch add/rename/delete (incl. each branch's own bank account) commit
            immediately inside KelolaCabangModal; nothing here is affected by Simpan/Batal. */}
        <div style={{ borderTop: "1px solid var(--border)", marginTop: 20, paddingTop: 16 }}>
          <h3 style={{ margin: "0 0 4px" }}>Cabang</h3>
          <div className="form-hint" style={{ marginBottom: "var(--gap-md)" }}>
            Tambah, ubah, atau hapus cabang, termasuk rekening bank masing-masing cabang.
            Perubahan di sini tersimpan langsung — tidak terpengaruh oleh Simpan/Batal di atas.
          </div>
          <button type="button" className="btn" onClick={() => setKelolaCabangOpen(true)}>Kelola Cabang</button>
        </div>

        {deviceLabel !== null && (
          <div style={{ borderTop: "1px solid var(--border)", marginTop: 20, paddingTop: 16 }}>
            <h3 style={{ margin: "0 0 4px" }}>Perangkat Ini</h3>
            <div className="form-hint" style={{ marginBottom: "var(--gap-md)" }}>
              Nama komputer ini, dicatat setiap kali menyimpan ke data bersama. Hanya disimpan di
              komputer ini, dan tersimpan langsung — tidak terpengaruh oleh Simpan/Batal di atas.
            </div>
            <div className="form-row">
              <label className="form-label" htmlFor="pengaturan-device">Nama Perangkat</label>
              <input id="pengaturan-device" className="form-input" value={deviceText} maxLength={60}
                placeholder="mis. Siti – laptop kasir" onChange={(e) => setDeviceText(e.target.value)} />
              {!deviceLabel && !deviceDirty && <div className="form-hint">Belum diberi nama — perubahan tercatat sebagai &ldquo;perangkat tanpa nama&rdquo;.</div>}
            </div>
            <button type="button" className="btn" disabled={!deviceDirty}
              onClick={() => onSaveDeviceLabel(deviceText)}>Simpan Nama Perangkat</button>
          </div>
        )}
      </div>

      {kelolaCabangOpen && (
        <KelolaCabangModal
          branches={branches} salespeople={salespeople} transactions={transactions}
          onAdd={onAddBranch} onUpdate={onUpdateBranch} onDelete={onDeleteBranch}
          onClose={() => setKelolaCabangOpen(false)}
        />
      )}
    </div>
  );
}
